import { Button, Checkbox, Space } from "@arco-design/web-react";
import { ChevronDown, ChevronUp } from "lucide-react";

import "./WorkbenchColumnSettings.css";

export type WorkbenchColumnOption<T extends string> = {
  key: T;
  label: string;
  /** 不可取消勾选。 */
  required?: boolean;
  /** 始终固定在末尾（如操作列），不可参与排序到中间。 */
  pinnedEnd?: boolean;
};

/** 去除重复列 key，保留首次出现顺序。 */
export function dedupeWorkbenchColumns<T extends string>(columns: T[]): T[] {
  return columns.filter((item, index) => columns.indexOf(item) === index);
}

/** 过滤非法列、补齐必选列，并保证 pinnedEnd 列在末尾；不强制改写其余列的相对顺序。 */
export function normalizeWorkbenchVisibleColumns<T extends string>(
  value: unknown,
  options: Array<WorkbenchColumnOption<T>>,
  defaults: T[],
): T[] {
  const allowedKeys = new Set(options.map((item) => item.key));
  const requiredKeys = options.filter((item) => item.required).map((item) => item.key);
  const pinnedEndKeys = options.filter((item) => item.pinnedEnd).map((item) => item.key);
  if (!Array.isArray(value)) {
    return defaults;
  }
  const filteredColumns = dedupeWorkbenchColumns(
    value.filter((item): item is T => typeof item === "string" && allowedKeys.has(item as T)),
  );
  const missingRequired = requiredKeys.filter((key) => !filteredColumns.includes(key));
  const withRequired = dedupeWorkbenchColumns([...missingRequired, ...filteredColumns]);
  const movable = withRequired.filter((key) => !pinnedEndKeys.includes(key));
  const pinnedPresent = pinnedEndKeys.filter((key) => withRequired.includes(key));
  const merged = [...movable, ...pinnedPresent];
  return merged.length ? merged : defaults;
}

/** 从 localStorage 读取列偏好。 */
export function loadWorkbenchVisibleColumnsPreference<T extends string>(
  storageKey: string,
  options: Array<WorkbenchColumnOption<T>>,
  defaults: T[],
): T[] {
  if (typeof window === "undefined") {
    return defaults;
  }
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return defaults;
    }
    return normalizeWorkbenchVisibleColumns(JSON.parse(rawValue), options, defaults);
  } catch {
    return defaults;
  }
}

/** 把列偏好写入 localStorage。 */
export function saveWorkbenchVisibleColumnsPreference<T extends string>(
  storageKey: string,
  columns: T[],
) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(columns));
  } catch {
    // 本地存储不可用时只保留当前页面状态。
  }
}

/** 按当前可见顺序排列选项，隐藏列追加在末尾。 */
export function orderWorkbenchColumnOptions<T extends string>(
  options: Array<WorkbenchColumnOption<T>>,
  visibleColumns: T[],
): Array<WorkbenchColumnOption<T>> {
  const optionByKey = new Map(options.map((item) => [item.key, item]));
  const visibleSet = new Set(visibleColumns);
  return [
    ...visibleColumns.map((key) => optionByKey.get(key)).filter(Boolean),
    ...options.filter((item) => !visibleSet.has(item.key)),
  ] as Array<WorkbenchColumnOption<T>>;
}

type WorkbenchColumnSettingsProps<T extends string> = {
  options: Array<WorkbenchColumnOption<T>>;
  visibleColumns: T[];
  onChange: (columns: T[]) => void;
};

/** 列表页通用列设置：显隐 + 上移/下移，顺序与表格列一致。 */
export function WorkbenchColumnSettings<T extends string>({
  options,
  visibleColumns,
  onChange,
}: WorkbenchColumnSettingsProps<T>) {
  const visibleSet = new Set(visibleColumns);
  const requiredKeys = options.filter((item) => item.required).map((item) => item.key);
  const pinnedEndKeys = new Set(options.filter((item) => item.pinnedEnd).map((item) => item.key));
  const orderedOptions = orderWorkbenchColumnOptions(options, visibleColumns);

  /** 切换列是否展示。 */
  function toggleColumn(key: T, checked: boolean) {
    if (checked) {
      onChange(
        normalizeWorkbenchVisibleColumns([...visibleColumns, key], options, visibleColumns),
      );
      return;
    }
    if (requiredKeys.includes(key)) {
      return;
    }
    onChange(
      normalizeWorkbenchVisibleColumns(
        visibleColumns.filter((item) => item !== key),
        options,
        visibleColumns,
      ),
    );
  }

  /** 调整列展示顺序；末尾固定列不可移入中间。 */
  function moveColumn(key: T, direction: -1 | 1) {
    if (pinnedEndKeys.has(key)) {
      return;
    }
    const movable = visibleColumns.filter((item) => !pinnedEndKeys.has(item));
    const index = movable.indexOf(key);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= movable.length) {
      return;
    }
    const nextMovable = [...movable];
    const [item] = nextMovable.splice(index, 1);
    nextMovable.splice(nextIndex, 0, item);
    const pinnedPresent = visibleColumns.filter((itemKey) => pinnedEndKeys.has(itemKey));
    onChange(
      normalizeWorkbenchVisibleColumns(
        [...nextMovable, ...pinnedPresent],
        options,
        visibleColumns,
      ),
    );
  }

  return (
    <div className="workbench-column-settings">
      {orderedOptions.map((item) => {
        const checked = visibleSet.has(item.key);
        const movableIndex = visibleColumns
          .filter((key) => !pinnedEndKeys.has(key))
          .indexOf(item.key);
        const movableCount = visibleColumns.filter((key) => !pinnedEndKeys.has(key)).length;
        const canMove = checked && !item.pinnedEnd;
        return (
          <div className="workbench-column-settings__row" key={item.key}>
            <Checkbox
              checked={checked}
              disabled={Boolean(item.required)}
              onChange={(nextChecked) => toggleColumn(item.key, Boolean(nextChecked))}
            >
              {item.label}
            </Checkbox>
            <Space size={4}>
              <Button
                size="mini"
                shape="circle"
                aria-label={`${item.label}上移`}
                disabled={!canMove || movableIndex <= 0}
                icon={<ChevronUp size={12} />}
                onClick={() => moveColumn(item.key, -1)}
              />
              <Button
                size="mini"
                shape="circle"
                aria-label={`${item.label}下移`}
                disabled={!canMove || movableIndex < 0 || movableIndex >= movableCount - 1}
                icon={<ChevronDown size={12} />}
                onClick={() => moveColumn(item.key, 1)}
              />
            </Space>
          </div>
        );
      })}
    </div>
  );
}
